import React, { useState } from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { Link, Text, Pill } from "@foris/avocado-suite";
import LinkRouter from "@common/components/link-router";
import { Icon } from "@foris/avocado-icons";
import { Group } from "@models/ISchema";
import scss from "./table.util.module.scss";
// Deep-path import to avocado-suite internals broke under Vite + TS 6 bundler
// resolution (CHS-2592). Derive PillProps from the Pill component itself.
type PillProps = React.ComponentProps<typeof Pill>;

export const getLinkCell = (route: string, id: string | null, value: string) => {
  return (
    <LinkRouter className={scss.columnLink} colored to={id ? `${route}/${id}` : route}>
      {value}
    </LinkRouter>
  );
};

export const capitalizeFirstLetter = (text?: string) => {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const getLinkDisabledCell = (value: string) => {
  return (
    <Text className={scss.columnLinkDisabled} weight="medium">
      {value}
    </Text>
  );
};

export const getColumnCell = (text: React.ReactNode, className = "") => {
  return <Text className={cx(scss.columnCell, className && className)}>{text}</Text>;
};

export const getColumnCellList = (entry: string[]): JSX.Element => {
  return (
    <ul>
      {entry?.map((change, index) => (
        <Text key={index}>{change}</Text>
      ))}
    </ul>
  );
};

export const getUserCell = (user: { name?: string; username?: string }) => {
  if (!user) return <Text>-</Text>;

  return (
    <div className={scss.columnUser}>
      <div className={scss.columnUser_avatar}>
        <Icon name="user" color="neutral-50" />
      </div>
      <div className={scss.columnUser_info}>
        <Text className={scss.username} type="sm">
          {user?.username}
        </Text>
        <Text className={scss.name} type="sm">
          {user?.name}
        </Text>
      </div>
    </div>
  );
};

export const getUsableCell = (usable: boolean) => {
  return usable ? <Icon name="check" /> : <Icon name="close" />;
};

export const getOpenGroupCell = (visible: boolean) => {
  return (
    <div className={scss.columnOpenGroup}>
      {visible ? <Icon name="open-eye" /> : <Icon name="closed-eye" />}
    </div>
  );
};

export const getHeadCell = (text: string) => text;

export const getPillCell = (text: string, props?: Omit<PillProps, "children">) => (
  <Pill
    color={props?.color}
    textColor={props?.textColor}
    iconColor={props?.iconColor}
    icon={props?.icon}
    className={scss.columnPill}
  >
    {text}
  </Pill>
);

export const getLabelCell = (labels: { id: string; name: string }[]) => {
  const visibleLabels = labels.slice(0, 2);
  const isMoreLables = labels.length > 2;

  return (
    <div className={scss.columnLabels}>
      {visibleLabels.map(label => (
        <Pill key={label.id}>{label.name}</Pill>
      ))}
      {isMoreLables && <Pill>+{labels.length - 2}</Pill>}
    </div>
  );
};

export const getMoreOrLessPillCell = (groups: Group[] | undefined | null) => {
  const { t } = useTranslation();
  const [showMore, setShowMore] = useState(false);

  const safeGroups = groups || [];
  const btnText = showMore
    ? t("package-link-management.table.btn-less")
    : t("package-link-management.table.btn-more");
  const dynamicIcon = showMore ? "chevron-up" : "chevron-down";
  const isMoreOrLessBtnEnabled = safeGroups.length > 3;
  const visibleGroups = showMore ? safeGroups : safeGroups.slice(0, 3);
  const isGroupsVisible = visibleGroups.length > 0;

  return (
    <div className={scss.columnMoreOrLess}>
      {isGroupsVisible
        ? visibleGroups.map(group => (
            <Pill className={scss.columnPill} key={group.id}>
              {group.code}
            </Pill>
          ))
        : getColumnCell("-")}

      {isMoreOrLessBtnEnabled && (
        <Link onClick={() => setShowMore(!showMore)} iconLeft={dynamicIcon} size="sm">
          {btnText}
        </Link>
      )}
    </div>
  );
};
